import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  Fab
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import toast from 'react-hot-toast';
import { getApiUrl } from '../config';

function Category() {
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(getApiUrl('/api/categories'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('خطا در دریافت دسته‌بندی‌ها');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editing 
        ? getApiUrl(`/api/categories/${editing.id}`)
        : getApiUrl('/api/categories');
      
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editing ? 'دسته‌بندی با موفقیت ویرایش شد' : 'دسته‌بندی با موفقیت ایجاد شد');
        setOpenDialog(false);
        resetForm();
        fetchCategories();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ذخیره دسته‌بندی');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('خطا در ذخیره دسته‌بندی');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این دسته‌بندی اطمینان دارید؟')) return;

    try {
      const res = await fetch(getApiUrl(`/api/categories/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (res.ok) {
        toast.success('دسته‌بندی با موفقیت حذف شد');
        fetchCategories();
      } else {
        toast.error('خطا در حذف دسته‌بندی');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('خطا در حذف دسته‌بندی');
    }
  };

  const handleEdit = (category) => {
    setEditing(category);
    setFormData({
      name: category.name,
      description: category.description || ''
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setEditing(null);
  };

  return (
    <Box sx={{ p: 3, fontFamily: 'Vazir' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <CategoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>
          مدیریت دسته‌بندی‌های بلاگ
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ fontFamily: 'Vazir' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>نام</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>توضیحات</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>تعداد پست‌ها</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow 
                  key={category.id}
                  sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  <TableCell sx={{ fontFamily: 'Vazir' }}>{category.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir' }}>{category.description}</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir' }}>{category.post_count || 0}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="ویرایش دسته‌بندی" sx={{ fontFamily: 'Vazir' }}>
                        <IconButton 
                          color="primary" 
                          onClick={() => handleEdit(category)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف دسته‌بندی" sx={{ fontFamily: 'Vazir' }}>
                        <IconButton 
                          color="error" 
                          onClick={() => handleDelete(category.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="افزودن دسته‌بندی جدید"
        onClick={() => setOpenDialog(true)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          fontFamily: 'Vazir',
          zIndex: 9999,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}
      >
        <AddIcon />
      </Fab>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>{editing ? 'ویرایش دسته‌بندی' : 'افزودن دسته‌بندی جدید'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="نام دسته‌بندی"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              sx={{ fontFamily: 'Vazir' }}
              inputProps={{ style: { fontFamily: 'Vazir' } }}
            />
            <TextField
              fullWidth
              label="توضیحات"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              sx={{ fontFamily: 'Vazir' }}
              inputProps={{ style: { fontFamily: 'Vazir' } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} sx={{ fontFamily: 'Vazir' }}>لغو</Button>
            <Button type="submit" variant="contained" sx={{ fontFamily: 'Vazir' }}>
              {editing ? 'ویرایش' : 'افزودن'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ fontFamily: 'Vazir' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Category; 